import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  Switch,
  FormControlLabel,
  CircularProgress,
  Chip,
  Divider,
  useTheme,
  TextField,
  Tooltip,
  Avatar
} from '@mui/material';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ReferenceLine
} from 'recharts';
import { format } from 'date-fns';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import CompareIcon from '@mui/icons-material/Compare';
import TuneIcon from '@mui/icons-material/Tune';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import InfoIcon from '@mui/icons-material/Info';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SensorTimeSeries from './SensorTimeSeries';
import TunnelSensorMap from './TunnelSensorMap';
import SimpleSensorChart from './SimpleSensorChart';
import useSensorData from '../hooks/useSensorData';
import { subDays } from 'date-fns';
import { getSensorPlacement } from '../api/sensorApi';

const timeRanges = [
  { value: 7, label: '7 Days' },
  { value: 30, label: '30 Days' },
  { value: 90, label: '90 Days' },
  { value: 180, label: '6 Months' },
  { value: 365, label: '1 Year' },
  { value: 'custom', label: 'Custom Range' }
];

const SensorDetailView = () => {
  // Only tunnelId remains in the URL
  const { sensorId, tunnelId } = useParams();
  
  // Use state to track selected section and reading type instead of URL
  const [selectedSection, setSelectedSection] = useState(null);
  
  // Move reading type to component state as well
  const [selectedReadingType, setSelectedReadingType] = useState('m1');
  const navigate = useNavigate();
  const theme = useTheme();
  
  // Define all state variables up front to avoid reference errors
  // Time range state
  const [timeRange, setTimeRange] = useState(365); // 1 year as default
  const [startDate, setStartDate] = useState(subDays(new Date(), 365));
  const [endDate, setEndDate] = useState(new Date());
  const [threshold, setThreshold] = useState(66);
  const [showThreshold, setShowThreshold] = useState(true);
  const [washDisplayMode, setWashDisplayMode] = useState('line'); // 'none', 'markers', 'line'
  const [smoothLines, setSmoothLines] = useState(false); // Disable smooth curve rendering by default
  const [cumulativeMode, setCumulativeMode] = useState(false); // Toggle for cumulative data visualization
  
  // Chart data state
  const [filteredData, setFilteredData] = useState([]);
  const [relevantWashDates, setRelevantWashDates] = useState([]);
  const [washTypes, setWashTypes] = useState({});  // Store wash types by date
  const [sensorPlacement, setSensorPlacement] = useState(null);
  const [sensorDetails, setSensorDetails] = useState({});
  const [selectedSensors, setSelectedSensors] = useState([]);
  const [filteredSensorsBySection, setFilteredSensorsBySection] = useState([]);
  
  // Reading type state
  const [currentReadingType, setCurrentReadingType] = useState('m1');
  const [previousReadingType, setPreviousReadingType] = useState(null);
  const [userClearedSelection, setUserClearedSelection] = useState(false);
  const [showAverage, setShowAverage] = useState(false); // Toggle for showing average of m1 and m2
  
  // Now use selectedReadingType as the source of truth
  const actualReadingType = currentReadingType || selectedReadingType || (sensorId === 'm1' || sensorId === 'm2' ? sensorId : 'm1');
  
  // Initialize with the tunnelId from URL parameters if available
  const { loading, data, error, selectedTunnel, changeTunnel, reloadData } = useSensorData(tunnelId);
  
  // Set selected tunnel when tunnelId changes
  useEffect(() => {
    if (tunnelId && tunnelId !== selectedTunnel) {
      changeTunnel(tunnelId);
    }
  }, [tunnelId, selectedTunnel, changeTunnel]);
  
  // Set the reading type on initial mount if sensorId is m1 or m2
  useEffect(() => {
    console.log('URL params - sensorId:', sensorId);
    
    // Only do this on initial mount or when sensorId actually changes
    if (sensorId === 'm1' || sensorId === 'm2') {
      console.log('Setting initial reading type from URL sensorId param:', sensorId);
      setCurrentReadingType(sensorId);
      setSelectedReadingType(sensorId);
    }
  }, [sensorId]);
  
  // Handle reading type changes from UI interactions
  // This is a separate effect to avoid conflicts with URL param handling
  
  useEffect(() => {
    // Skip first render
    if (previousReadingType === null) {
      setPreviousReadingType(currentReadingType);
      return;
    }
    
    // Check if reading type actually changed
    if (currentReadingType !== previousReadingType) {
      console.log(`Reading type changed from ${previousReadingType} to ${currentReadingType}`);
      setPreviousReadingType(currentReadingType);
      
      // IMPORTANT: Do NOT reset user selection state here
      // This preserves the user's choice to clear selections when switching reading types
      // setUserClearedSelection(false);
      
      // Force filtered data to update based on the new reading type
      // We'll re-filter the data with the current date range and new reading type
      if (data?.sensorData) {
        console.log(`Re-filtering data with new reading type: ${currentReadingType}`);
        
        // Force a small delay to ensure React processes everything in order
        setTimeout(() => {
          // Filter data based on current settings
          const filtered = data.sensorData.filter(item => {
            const itemDate = new Date(item.timestamp);
            return itemDate >= startDate && itemDate <= endDate;
          });
          setFilteredData(filtered);
        }, 0);
      }
    }
  }, [currentReadingType, previousReadingType, data, startDate, endDate]);
  
  // Filter sensors by section ID if provided
  
  // Set up filtering by section when applicable
  useEffect(() => {
    if (selectedSection && data?.originalData) {
      // We need to filter sensors that belong to this section
      const filterSensorsBySection = async () => {
        try {
          const response = await fetch('/data/raw/sensor-placement.json');
          if (response.ok) {
            const placementData = await response.json();
            // Find the placement entry matching tunnel and section
            const placement = placementData.find(p => 
              p.placement === selectedSection && 
              (!tunnelId || p.tunnel === tunnelId)
            );
            
            if (placement) {
              // Extract all sensors from this placement/section
              const sensorsInSection = [];
              for (const [position, sensors] of Object.entries(placement.sensors)) {
                sensorsInSection.push(...sensors);
              }
              setFilteredSensorsBySection(sensorsInSection);
            }
          }
        } catch (error) {
          console.error("Error filtering sensors by section:", error);
        }
      };
      
      filterSensorsBySection();
    } else {
      setFilteredSensorsBySection([]);
    }
  }, [selectedSection, tunnelId, data]);
  
  // State variables already declared at the top of the component
  
  // Set threshold from data when available
  useEffect(() => {
    if (data?.threshold) {
      setThreshold(data.threshold);
    }
  }, [data]);
  
  // Process wash dates and types
  useEffect(() => {
    if (data?.washDates && data?.washTypes) {
      const relevant = data.washDates.filter(dateStr => {
        const washDate = new Date(dateStr);
        return washDate >= startDate && washDate <= endDate;
      });
      
      setRelevantWashDates(relevant);
      setWashTypes(data.washTypes);
      
      console.log(`Processed ${relevant.length} wash dates in selected time range`);
    }
  }, [data, startDate, endDate]);
  
  // State to track if user has manually cleared sensor selection
  // (already declared at the top of the component)
  
  // Function to clear selected sensors while setting the user flag
  const clearSelectedSensors = () => {
    setSelectedSensors([]);
    setUserClearedSelection(true);
  };
  
  // Initialize selected sensors when sensor details change
  useEffect(() => {
    // Only auto-select sensors if:
    // 1. We have actual sensors in the data
    // 2. No sensors are currently selected
    // 3. The user hasn't manually cleared the selection using "Clear All" or "Show Average"
    if (sensorDetails.actualSensors && 
        sensorDetails.actualSensors.length > 0 && 
        selectedSensors.length === 0 && 
        !userClearedSelection) {
      
      console.log('Considering auto-selection of sensors - userClearedSelection:', userClearedSelection);
      console.log('SensorDetailView - data available:', {
        hasOriginalData: !!data?.originalData,
        originalDataLength: data?.originalData?.length || 0,
        sensorCount: sensorDetails.actualSensors.length,
        readingType: actualReadingType
      });
      
      // If we have filtered sensors by section, select those
      if (filteredSensorsBySection.length > 0) {
        const sensorsToSelect = sensorDetails.actualSensors.filter(
          sensorCode => filteredSensorsBySection.includes(sensorCode)
        );
        
        if (sensorsToSelect.length > 0) {
          console.log('Auto-selecting sensors for section:', sensorsToSelect);
          setSelectedSensors(sensorsToSelect);
          return;
        }
      }
      
      // Default to selecting sensors with the roof position (typically 1 per placement)
      const roofSensors = sensorDetails.actualSensors.filter(sensorCode => {
        const placement = sensorDetails.placements[sensorCode];
        return placement && placement.position === 'roof';
      });
      
      if (roofSensors.length > 0) {
        console.log('Auto-selecting roof sensors:', roofSensors);
        setSelectedSensors(roofSensors);
      }
    } else if (userClearedSelection) {
      console.log('Not auto-selecting sensors because user manually cleared selection');
    }
  }, [sensorDetails, selectedSensors, data, filteredSensorsBySection, userClearedSelection, actualReadingType]);
  
  // Get sensor placement information
  useEffect(() => {
    const loadSensorDetails = async () => {
      if (data?.originalData) {
        console.log('Loading sensor details with reading type:', actualReadingType);
      
        // Find the actual sensor(s) in the data by looking for patterns in the data
        const actualSensors = [...new Set(data.originalData
          .filter(item => item.sensor)
          .map(item => item.sensor))];
          
        console.log('Found actual sensors in data:', actualSensors);
          
        // Collect placement info for all sensors
        const placements = {};
        const sensorData = {}; // Store the actual data for each sensor
        
        for (const sensorCode of actualSensors) {
          try {
            const placement = await getSensorPlacement(sensorCode);
            if (placement) {
              placements[sensorCode] = placement;
              
              // Auto-select the tunnel if none is selected
              if (!selectedTunnel && placement.tunnel) {
                console.log('Auto-selecting tunnel from sensor placement:', placement.tunnel);
                changeTunnel(placement.tunnel);
              }
            }
            
            // Extract data specific to this sensor
            // Include BOTH m1 and m2 values so we can switch between them without reloading
            const sensorReadings = data.originalData
              .filter(item => item.sensor === sensorCode)
              .map(item => ({
                timestamp: item.timestamp,
                m1: item.m1,
                m2: item.m2,
                // Also include the current reading type for convenience
                value: item[actualReadingType]
              }));
              
            sensorData[sensorCode] = sensorReadings;
          } catch (err) {
            console.error(`Error loading placement for ${sensorCode}:`, err);
          }
        }
        
        // When reading type changes, reset the user selection flag
        if (actualReadingType !== currentReadingType) {
          setUserClearedSelection(false);
        }
        
        setSensorDetails({
          actualSensors,
          placements,
          sensorData
        });
      }
    };
    
    loadSensorDetails();
  }, [data, selectedTunnel, changeTunnel, actualReadingType, currentReadingType]);
  
  // Reference to track updates
  const updateCount = useRef(0);
  
  // Handle date updates separately to avoid cascading state updates
  useEffect(() => {
    if (timeRange !== 'custom') {
      const start = subDays(new Date(), timeRange);
      const end = new Date();
      setStartDate(start);
      setEndDate(end);
    }
  }, [timeRange]);
  
  // Calculate average of m1 and m2 values
  const calculateAverages = (dataArray) => {
    if (!dataArray || dataArray.length === 0) return [];
    
    // Create a deep copy of the data to avoid modifying the original
    const result = JSON.parse(JSON.stringify(dataArray));
    
    // For each data point, calculate the average of m1 and m2
    result.forEach(item => {
      const m1 = item.m1;
      const m2 = item.m2;
      
      // Calculate average only if both values are present
      if (m1 !== null && m1 !== undefined && !isNaN(m1) && 
          m2 !== null && m2 !== undefined && !isNaN(m2)) {
        item.avg = (m1 + m2) / 2;
      } else if (m1 !== null && m1 !== undefined && !isNaN(m1)) {
        // If only m1 is available, use that
        item.avg = m1;
      } else if (m2 !== null && m2 !== undefined && !isNaN(m2)) {
        // If only m2 is available, use that
        item.avg = m2;
      } else {
        item.avg = null;
      }
      
      // Also process any sensor-specific data if available
      Object.keys(item).forEach(key => {
        // Check if this is a sensor key (not a system property or other data)
        if (key !== 'timestamp' && key !== 'm1' && key !== 'm2' && key !== 'avg' &&
            !key.startsWith('washStatus') && !key.includes('_m1') && !key.includes('_m2')) {
          
          // Try to find corresponding m1 and m2 readings for this sensor
          const sensorM1Key = `${key}_m1`;
          const sensorM2Key = `${key}_m2`;
          
          const sensorM1 = item[sensorM1Key];
          const sensorM2 = item[sensorM2Key];
          
          // Calculate average for this sensor if both values are present
          if (sensorM1 !== null && sensorM1 !== undefined && !isNaN(sensorM1) && 
              sensorM2 !== null && sensorM2 !== undefined && !isNaN(sensorM2)) {
            item[`${key}_avg`] = (sensorM1 + sensorM2) / 2;
          } else if (sensorM1 !== null && sensorM1 !== undefined && !isNaN(sensorM1)) {
            // If only m1 is available, use that
            item[`${key}_avg`] = sensorM1;
          } else if (sensorM2 !== null && sensorM2 !== undefined && !isNaN(sensorM2)) {
            // If only m2 is available, use that
            item[`${key}_avg`] = sensorM2;
          }
        }
      });
    });
    
    return result;
  };
  
  // Transform data to cumulative mode using the pre-wash value as reference
  const transformToCumulativeData = (dataArray, washDates) => {
    if (!dataArray || dataArray.length === 0) return [];
    
    // Create a deep copy of the data to avoid modifying the original
    const result = JSON.parse(JSON.stringify(dataArray));
    
    // Convert wash dates strings to Date objects for comparison and sort them
    const washDateObjects = washDates
      .map(dateStr => new Date(dateStr))
      .sort((a, b) => a - b); // Ensure chronological order
    
    if (washDateObjects.length === 0) {
      return result;
    }
    
    // We'll split the data into segments based on wash dates
    const segments = [];
    let startIndex = 0;
    
    // Find the start and end indices for each segment between washes
    for (let washIdx = 0; washIdx < washDateObjects.length; washIdx++) {
      const washDate = washDateObjects[washIdx];
      let endIndex = -1;
      let nextStartIndex = -1;
      
      // Find the first data point after the current wash date
      for (let i = startIndex; i < result.length; i++) {
        const itemDate = new Date(result[i].timestamp);
        
        if (itemDate >= washDate) {
          endIndex = i - 1; // Last item before wash
          nextStartIndex = i; // First item after wash
          break;
        }
      }
      
      // If we didn't find a data point after this wash, use the last available data point
      if (endIndex === -1) {
        endIndex = result.length - 1;
      }
      
      // Add the segment before the wash
      segments.push({
        start: startIndex,
        end: endIndex,
        washDate,
        isPreWash: true
      });
      
      // If we found the point after wash, start a new segment
      if (nextStartIndex !== -1) {
        startIndex = nextStartIndex;
      } else {
        // If we couldn't find the next segment start, we're done
        break;
      }
      
      // For the last wash, add a final segment for all data after it
      if (washIdx === washDateObjects.length - 1) {
        segments.push({
          start: startIndex,
          end: result.length - 1,
          washDate,
          isPreWash: false
        });
      }
    }
    
    // Split data into segments based on wash dates
    
    // Now process each segment
    const adjustments = {
      m1: 0,
      m2: 0,
      avg: 0
    };
    
    const sensorAdjustments = {}; // For sensor-specific values
    
    // Find all sensor values in the dataset
    const sensorKeys = new Set();
    result.forEach(item => {
      Object.keys(item).forEach(key => {
        if (key !== 'timestamp' && 
            !key.startsWith('washStatus') && 
            key !== 'm1' && 
            key !== 'm2' && 
            key !== 'avg' &&
            item[key] !== null && 
            item[key] !== undefined && 
            !isNaN(item[key])) {
          sensorKeys.add(key);
          sensorAdjustments[key] = 0;
        }
      });
    });
    
    // Process all segments after the first one
    // We don't need to adjust the first segment (before the first wash)
    for (let segIdx = 1; segIdx < segments.length; segIdx++) {
      const segment = segments[segIdx];
      const prevSegment = segments[segIdx - 1];
      
      // Define the window to look for the lowest point after wash (up to 5 days)
      const POST_WASH_WINDOW_DAYS = 5;
      const windowEndIndex = Math.min(
        segment.end,
        findIndexNDaysAfter(result, segment.start, POST_WASH_WINDOW_DAYS)
      );
      
      // Define the window to look for highest point before wash (up to 5 days)
      const PRE_WASH_WINDOW_DAYS = 5;
      const preWashStartIndex = Math.max(
        prevSegment.start,
        findIndexNDaysBefore(result, prevSegment.end, PRE_WASH_WINDOW_DAYS)
      );
      
      
      // Find highest value of the previous segment (just before wash)
      const preWashValues = {
        m1: null,
        m2: null,
        avg: null
      };
      
      const preSensorValues = {};
      sensorKeys.forEach(key => {
        preSensorValues[key] = null;
      });
      
      // Get the highest values from the pre-wash window
      for (let i = preWashStartIndex; i <= prevSegment.end; i++) {
        const item = result[i];
        
        // Process m1, m2, and avg
        ['m1', 'm2', 'avg'].forEach(key => {
          if (item[key] !== null && item[key] !== undefined && !isNaN(item[key])) {
            if (preWashValues[key] === null || item[key] > preWashValues[key]) {
              preWashValues[key] = item[key];
            }
          }
        });
        
        // Process sensor-specific values
        sensorKeys.forEach(key => {
          if (item[key] !== null && item[key] !== undefined && !isNaN(item[key])) {
            if (preSensorValues[key] === null || item[key] > preSensorValues[key]) {
              preSensorValues[key] = item[key];
            }
          }
        });
      }
      
      // Find lowest value of the current segment window (right after wash)
      const postWashValues = {
        m1: null,
        m2: null,
        avg: null
      };
      
      const postSensorValues = {};
      sensorKeys.forEach(key => {
        postSensorValues[key] = null;
      });
      
      // Get the lowest values from the post-wash window
      for (let i = segment.start; i <= windowEndIndex; i++) {
        const item = result[i];
        
        // Process m1, m2, and avg
        ['m1', 'm2', 'avg'].forEach(key => {
          if (item[key] !== null && item[key] !== undefined && !isNaN(item[key])) {
            if (postWashValues[key] === null || item[key] < postWashValues[key]) {
              postWashValues[key] = item[key];
            }
          }
        });
        
        // Process sensor-specific values
        sensorKeys.forEach(key => {
          if (item[key] !== null && item[key] !== undefined && !isNaN(item[key])) {
            if (postSensorValues[key] === null || item[key] < postSensorValues[key]) {
              postSensorValues[key] = item[key];
            }
          }
        });
      }
      
      // Note: We're not using the adjustments/sensorAdjustments variables here anymore
      // We calculate segment-specific adjustments below instead
      
      // Calculate segment-specific adjustments for just this segment
      const segmentAdjustments = {
        m1: 0,
        m2: 0,
        avg: 0
      };
      
      const segmentSensorAdjustments = {};
      sensorKeys.forEach(key => {
        segmentSensorAdjustments[key] = 0;
      });
      
      // Calculate individual adjustments for this segment only
      ['m1', 'm2', 'avg'].forEach(key => {
        if (preWashValues[key] !== null && postWashValues[key] !== null) {
          // Add the difference between pre-wash high and post-wash low
          const segmentDiff = preWashValues[key] - postWashValues[key];
          
          // Only apply if there's a significant drop (avoid small fluctuations)
          if (segmentDiff > 3) {
            segmentAdjustments[key] = segmentDiff;
          }
        }
      });
      
      // Calculate sensor-specific segment adjustments - optimize with array iteration
      const sensorKeysArray = Array.from(sensorKeys);
      for (let j = 0; j < sensorKeysArray.length; j++) {
        const key = sensorKeysArray[j];
        const preValue = preSensorValues[key];
        const postValue = postSensorValues[key];
        
        if (preValue !== null && preValue !== undefined && !isNaN(preValue) &&
            postValue !== null && postValue !== undefined && !isNaN(postValue)) {
          // Add the difference between pre-wash high and post-wash low
          const segmentDiff = preValue - postValue;
          
          // Only apply if there's a significant drop (avoid small fluctuations)
          if (segmentDiff > 3) {
            segmentSensorAdjustments[key] = segmentDiff;
          }
        }
      }
      
      // Apply just this segment's adjustments to this segment and all future segments
      for (let i = segment.start; i <= result.length - 1; i++) {
        const item = result[i];
        
        // Apply adjustments to m1, m2, and avg - optimized to avoid unnecessary checks
        for (const key of ['m1', 'm2', 'avg']) {
          const adjustment = segmentAdjustments[key];
          if (adjustment && item[key] !== null && item[key] !== undefined && !isNaN(item[key])) {
            item[key] += adjustment;
          }
        }
        
        // Apply sensor-specific adjustments using array iteration instead of forEach
        const keys = Array.from(sensorKeys);
        for (let j = 0; j < keys.length; j++) {
          const key = keys[j];
          const adjustment = segmentSensorAdjustments[key];
          if (adjustment && item[key] !== null && item[key] !== undefined && !isNaN(item[key])) {
            item[key] += adjustment;
          }
        }
      }
      
      // Track running totals for reporting at the end
      ['m1', 'm2', 'avg'].forEach(key => {
        adjustments[key] += segmentAdjustments[key];
      });
      
      sensorKeys.forEach(key => {
        sensorAdjustments[key] += segmentSensorAdjustments[key];
      });
    }
    
    // Log summary of adjustments
    console.log('Cumulative adjustment summary:');
    console.log('- m1 adjustment:', adjustments.m1.toFixed(2));
    console.log('- m2 adjustment:', adjustments.m2.toFixed(2));
    console.log('- avg adjustment:', adjustments.avg.toFixed(2));
    console.log('- Sensors adjusted:', sensorKeys.size);
    
    // Sample the first and last data points to verify transformation
    if (result.length > 0) {
      const first = result[0];
      const last = result[result.length - 1];
      console.log('First data point (should be unchanged):', {
        timestamp: first.timestamp,
        m1: first.m1,
        m2: first.m2
      });
      console.log('Last data point (should include all adjustments):', {
        timestamp: last.timestamp,
        m1: last.m1,
        m2: last.m2
      });
    }
    
    return result;
  };
  
  // Helper function to find the index of a data point N days after a given index
  const findIndexNDaysAfter = (data, startIndex, days) => {
    if (startIndex >= data.length) return data.length - 1;
    
    const startDate = new Date(data[startIndex].timestamp);
    const targetDate = new Date(startDate);
    targetDate.setDate(targetDate.getDate() + days);
    
    for (let i = startIndex; i < data.length; i++) {
      const itemDate = new Date(data[i].timestamp);
      if (itemDate >= targetDate) {
        return i;
      }
    }
    
    return data.length - 1; // If we don't find a date N days later, return the last index
  };
  
  // Helper function to find the index of a data point N days before a given index
  const findIndexNDaysBefore = (data, endIndex, days) => {
    if (endIndex < 0) return 0;
    
    const endDate = new Date(data[endIndex].timestamp);
    const targetDate = new Date(endDate);
    targetDate.setDate(targetDate.getDate() - days);
    
    for (let i = endIndex; i >= 0; i--) {
      const itemDate = new Date(data[i].timestamp);
      if (itemDate <= targetDate) {
        return i;
      }
    }
    
    return 0; // If we don't find a date N days earlier, return the first index
  };

  // Apply balanced smoothing to data that preserves key features
  const applySmoothing = (dataArray, windowSize = 15) => {
    if (!dataArray || dataArray.length === 0) return [];
    if (dataArray.length <= 3) return dataArray;
    
    // Clone the array to avoid modifying the original
    const result = JSON.parse(JSON.stringify(dataArray));
    
    // Identify all numeric fields that need smoothing
    const numericFields = new Set();
    dataArray.forEach(item => {
      Object.entries(item).forEach(([key, value]) => {
        if (typeof value === 'number' && !isNaN(value)) {
          numericFields.add(key);
        }
      });
    });
    
    // For each numeric field, apply a feature-preserving smoothing
    numericFields.forEach(fieldName => {
      // Get valid data points for this field
      const validPoints = [];
      for (let i = 0; i < dataArray.length; i++) {
        if (typeof dataArray[i][fieldName] === 'number' && !isNaN(dataArray[i][fieldName])) {
          validPoints.push({
            index: i,
            value: dataArray[i][fieldName]
          });
        }
      }
      
      if (validPoints.length <= 5) return; // Not enough data to smooth
      
      // Calculate original statistics for feature detection
      let sumOriginal = 0;
      let minOriginal = Number.MAX_VALUE;
      let maxOriginal = Number.MIN_VALUE;
      
      validPoints.forEach(point => {
        sumOriginal += point.value;
        minOriginal = Math.min(minOriginal, point.value);
        maxOriginal = Math.max(maxOriginal, point.value);
      });
      
      const avgOriginal = sumOriginal / validPoints.length;
      const rangeOriginal = maxOriginal - minOriginal;
      
      // APPROACH 1: Apply modified moving average with adaptive window
      for (let i = 0; i < validPoints.length; i++) {
        const pointIndex = validPoints[i].index;
        let sum = 0;
        let sumWeights = 0;
        
        // Detect significant value changes
        const currentVal = validPoints[i].value;
        const significanceThreshold = rangeOriginal * 0.15; // 15% of data range is considered significant
        
        // Check if current point is a peak, valley, or other significant feature
        let isSignificantPoint = false;
        if (i > 0 && i < validPoints.length - 1) {
          const prevVal = validPoints[i-1].value;
          const nextVal = validPoints[i+1].value;
          
          // Significant change detection algorithm
          // 1. Detect peaks and valleys (local extrema)
          const isPeak = currentVal > prevVal && currentVal > nextVal;
          const isValley = currentVal < prevVal && currentVal < nextVal;
          
          // 2. Detect significant magnitude changes
          const prevDiff = Math.abs(currentVal - prevVal);
          const nextDiff = Math.abs(nextVal - currentVal);
          const isSignificantChange = prevDiff > significanceThreshold || nextDiff > significanceThreshold;
          
          // 3. Identify points far from the mean
          const distanceFromMean = Math.abs(currentVal - avgOriginal);
          const isFarFromMean = distanceFromMean > rangeOriginal * 0.25; // 25% of range from mean
          
          isSignificantPoint = (isPeak || isValley) && (isSignificantChange || isFarFromMean);
        }
        
        // Use a smaller window for significant points to preserve them
        const adaptiveWindow = isSignificantPoint ? Math.max(3, Math.floor(windowSize / 3)) : windowSize;
        
        // Apply weighted moving average with the adaptive window
        for (let j = Math.max(0, i - adaptiveWindow); j <= Math.min(validPoints.length - 1, i + adaptiveWindow); j++) {
          const distance = Math.abs(i - j);
          const maxDistance = adaptiveWindow;
          
          // Weight calculation - triangular kernel for moderate smoothing
          // Closer points get higher weight
          const weight = isSignificantPoint ? 
                        (j === i ? 3.0 : (1.0 - distance / (maxDistance + 1))) : // Preserve significant point
                        (1.0 - distance / (maxDistance + 1)); // Standard triangular
          
          sum += validPoints[j].value * weight;
          sumWeights += weight;
        }
        
        if (sumWeights > 0) {
          result[pointIndex][fieldName] = sum / sumWeights;
        }
      }
      
      // APPROACH 2: Feature-preserving velocity smoothing
      // Particularly aimed at preserving dips and peaks while smoothing transition areas
      const smoothedValues = [];
      for (let i = 0; i < validPoints.length; i++) {
        smoothedValues.push(result[validPoints[i].index][fieldName]);
      }
      
      // Apply adaptive smoothing based on velocity
      const tempValues = [...smoothedValues];
      for (let i = 2; i < smoothedValues.length - 2; i++) {
        // Calculate local velocities (first derivatives)
        const v1 = smoothedValues[i] - smoothedValues[i-1];
        const v2 = smoothedValues[i+1] - smoothedValues[i];
        
        // Preserve points where direction changes (peaks and valleys)
        const isDirectionChange = (v1 * v2 <= 0);
        
        // Less aggressive smoothing for direction changes to preserve important features
        if (isDirectionChange) {
          // Low smoothing at direction changes (peaks and valleys)
          tempValues[i] = 0.6 * smoothedValues[i] + 
                         0.2 * smoothedValues[i-1] + 
                         0.2 * smoothedValues[i+1];
        } else {
          // More aggressive smoothing for steady trends
          tempValues[i] = 0.4 * smoothedValues[i] + 
                         0.15 * smoothedValues[i-2] +
                         0.15 * smoothedValues[i-1] + 
                         0.15 * smoothedValues[i+1] + 
                         0.15 * smoothedValues[i+2];
        }
      }
      
      // Update smoothed values
      for (let i = 2; i < smoothedValues.length - 2; i++) {
        smoothedValues[i] = tempValues[i];
      }
      
      // Update the result with the feature-preserving smoothed values
      for (let i = 0; i < validPoints.length; i++) {
        result[validPoints[i].index][fieldName] = smoothedValues[i];
      }
      
      // APPROACH 3: Selective EMA smoothing with higher alpha
      // This provides moderate smoothing while preserving trends
      const alpha = 0.2; // Moderate alpha preserves more features
      let lastValue = result[validPoints[0].index][fieldName];
      
      for (let i = 1; i < validPoints.length; i++) {
        const pointIndex = validPoints[i].index;
        const currentVal = result[pointIndex][fieldName];
        const emaValue = alpha * currentVal + (1 - alpha) * lastValue;
        
        // Detect significant changes that should be preserved
        const changeSize = Math.abs(currentVal - lastValue);
        const isSignificantChange = changeSize > (rangeOriginal * 0.1); // 10% of range is significant
        
        // Use less smoothing for significant changes
        if (isSignificantChange) {
          // Blend original and EMA with bias toward original to preserve feature
          result[pointIndex][fieldName] = 0.7 * currentVal + 0.3 * emaValue;
        } else {
          // Use standard EMA for normal variations
          result[pointIndex][fieldName] = emaValue;
        }
        
        lastValue = result[pointIndex][fieldName];
      }
    });
    
    return result;
  };

  // Track the original filtered data and smoothed data separately
  const [originalFilteredData, setOriginalFilteredData] = useState([]);

  // Filter data based on selected time range and reading type
  // This is a separate effect to avoid re-rendering loops when dates are updated
  useEffect(() => {
    if (!data?.sensorData) return;

    // Debug log API data received
    console.log('SensorDetailView filtering data:', {
      selectedTunnel: data.selectedTunnel || 'not set',
      sensorDataLength: data.sensorData.length,
      readingType: actualReadingType,
      dateRange: `${startDate.toISOString().split('T')[0]} to ${endDate.toISOString().split('T')[0]}`
    });

    // Filter sensor data based on selected date range
    let filtered = data.sensorData.filter(item => {
      const itemDate = new Date(item.timestamp);
      return itemDate >= startDate && itemDate <= endDate;
    });
    
    // CRITICAL: Check if we need to transform data to display sections properly
    // For sections, we need to map each sensor's data from the original data
    // to include both m1 and m2 readings, and then use the current reading type
    if (selectedSection && data.originalData && filtered.length > 0) {
      console.log(`Processing section ${selectedSection} data with reading type: ${actualReadingType}`);
      console.log(`Found ${filteredSensorsBySection.length} sensors in section ${selectedSection}:`, filteredSensorsBySection);
      
      // First, make sure we have the original data organized by sensor and timestamp
      const sensorDataByDate = {};
      
      // Process the original data to group by sensor and date
      data.originalData.forEach(item => {
        if (!item.sensor || !item.timestamp) return;
        
        const date = item.timestamp.split('T')[0]; // Just use the date part for matching
        const sensorId = item.sensor;
        
        if (!sensorDataByDate[date]) {
          sensorDataByDate[date] = {};
        }
        
        // Store both m1 and m2 readings for this sensor on this date
        sensorDataByDate[date][sensorId] = {
          m1: item.m1,
          m2: item.m2
        };
      });
      
      console.log('Built sensor data lookup by date, sample:', 
        Object.keys(sensorDataByDate).slice(0, 2).map(date => ({ 
          date, 
          sensors: Object.keys(sensorDataByDate[date]).length 
        }))
      );
      
      // Now process each filtered data point to include sensor readings
      let hasSensorReadings = false;
      
      // Then, for each data point, include the correct reading type for each sensor
      filtered = filtered.map(dataPoint => {
        const result = { ...dataPoint }; // Copy original properties
        const date = dataPoint.timestamp.split('T')[0]; // Get date part for lookup
        
        // Skip if we don't have sensor data for this date
        if (!sensorDataByDate[date]) return result;
        
        // For each sensor in this section, add its values with the current reading type
        filteredSensorsBySection.forEach(sensorId => {
          const sensorData = sensorDataByDate[date]?.[sensorId];
          
          if (sensorData) {
            // Get the value for the current reading type
            const value = sensorData[actualReadingType];
            
            // Check if we already have a special key in the original data
            // The API may have created keys like "sensorId_m1" and "sensorId_m2"
            const specialKey = `${sensorId}_${actualReadingType}`;
            
            if (dataPoint[specialKey] !== undefined && 
                dataPoint[specialKey] !== null && 
                !isNaN(dataPoint[specialKey])) {
              
              // Use the value from the special key
              result[sensorId] = dataPoint[specialKey];
              console.log(`Using special key ${specialKey} for sensor ${sensorId}: ${result[sensorId]}`);
              hasSensorReadings = true;
            }
            // Otherwise use the value from our lookup
            else if (value !== null && value !== undefined && !isNaN(value)) {
              result[sensorId] = value;
              console.log(`Using extracted value for sensor ${sensorId} (${actualReadingType}): ${value}`);
              hasSensorReadings = true;
            }
          }
        });
        
        return result;
      });
      
      // Log detailed info about the first data point to help diagnose
      if (filtered.length > 0) {
        const samplePoint = filtered[0];
        const date = samplePoint.timestamp.split('T')[0];
        
        console.log(`Sample data point for date ${date}:`, samplePoint);
        console.log(`Reading type: ${actualReadingType}`);
        
        // Check which sensors have values
        const sensorsWithValues = filteredSensorsBySection.filter(
          sensorId => samplePoint[sensorId] !== undefined
        );
        
        console.log(`Sensors with values (${sensorsWithValues.length}):`, sensorsWithValues);
        
        if (sensorsWithValues.length === 0) {
          console.log('Sensor data available for this date:', 
            Object.keys(sensorDataByDate[date] || {})
          );
        }
      }
      
      console.log(`Processed section ${selectedSection} data - has sensor readings:`, hasSensorReadings);
    }
    
    // CRITICAL: Validate if we have data with the current reading type
    const hasSensorData = filtered.some(item => 
      item[actualReadingType] !== null && item[actualReadingType] !== undefined && !isNaN(item[actualReadingType])
    );
    
    console.log(`Has ${actualReadingType} values:`, hasSensorData);
    
    if (!hasSensorData && filtered.length > 0) {
      console.warn(`No valid ${actualReadingType} values in filtered data - may not display properly`);
      
      // Add detailed logging about what data we do have
      const keys = Object.keys(filtered[0]);
      console.log('Available keys in data:', keys);
      console.log('First data point:', filtered[0]);
      
      // Check if we have m1/m2 in the data set
      const hasM1 = filtered.some(item => item.m1 !== null && item.m1 !== undefined);
      const hasM2 = filtered.some(item => item.m2 !== null && item.m2 !== undefined);
      console.log('Chart data check - Has m1:', hasM1, 'Has m2:', hasM2);
    }
    
    // Debug log to check data, but limit frequency
    updateCount.current += 1;
    if (updateCount.current % 5 === 0) {
      console.log(`SensorDetailView update #${updateCount.current} - Selected tunnel:`, selectedTunnel);
      console.log('Filtered data length:', filtered.length);
      if (filtered.length > 0) {
        console.log('Sample filtered data:', filtered[0]);
        console.log(`Has values for ${actualReadingType}:`, filtered.some(item => item[actualReadingType] !== null));
      }
    }
    
    // Store the original filtered data
    setOriginalFilteredData(filtered);
    
    // Calculate averages for each data point
    filtered = calculateAverages(filtered);
    
    // Apply cumulative transformation if enabled
    if (cumulativeMode && filtered.length > 0) {
      console.log('Applying cumulative transformation');
      filtered = transformToCumulativeData(filtered, relevantWashDates);
    }
    
    // Apply smoothing if the toggle is on
    if (smoothLines && filtered.length > 0) {
      // Use moderate window sizes that preserve important features
      const windowSize = filtered.length > 300 ? 25 : 
                        (filtered.length > 200 ? 20 : 
                        (filtered.length > 100 ? 15 : 10));
      console.log(`Applying feature-preserving smoothing with window size ${windowSize}`);
      const smoothedData = applySmoothing(filtered, windowSize);
      setFilteredData(smoothedData);
    } else {
      // Use unsmoothed data
      setFilteredData(filtered);
    }
    
    // Filter wash dates to the selected date range (data is already sanitized)
    if (data.washDates) {
      const relevantWashes = data.washDates.filter(date => {
        const washDate = new Date(date);
        return washDate >= startDate && washDate <= endDate;
      });
      
      console.log(`Filtered to ${relevantWashes.length} wash dates within selected date range`);
      setRelevantWashDates(relevantWashes);
    }
  // Remove relevantWashDates from dependencies to prevent infinite loop
  }, [data, startDate, endDate, actualReadingType, selectedTunnel, currentReadingType, 
      selectedSection, selectedReadingType, filteredSensorsBySection, showAverage, 
      cumulativeMode, /* relevantWashDates removed */ smoothLines]);
  
  const handleTimeRangeChange = (event) => {
    setTimeRange(event.target.value);
  };
  
  const handleThresholdChange = (event, newValue) => {
    setThreshold(newValue);
  };
  
  // Handle toggling of data smoothing
  const handleSmoothingChange = (event) => {
    const newSmoothingState = event.target.checked;
    setSmoothLines(newSmoothingState);
    
    // We'll need to apply both averaging and smoothing
    if (originalFilteredData.length > 0) {
      // Always calculate averages first
      const dataWithAverages = calculateAverages(originalFilteredData);
      
      // Then apply smoothing if enabled
      if (newSmoothingState) {
        const windowSize = dataWithAverages.length > 300 ? 25 : 
                          (dataWithAverages.length > 200 ? 20 : 
                          (dataWithAverages.length > 100 ? 15 : 10));
        console.log(`Toggling feature-preserving smoothing ON with window size ${windowSize}`);
        const smoothedData = applySmoothing(dataWithAverages, windowSize);
        setFilteredData(smoothedData);
      } else {
        // Use unsmoothed data with averages
        console.log('Toggling smoothing OFF, using averaged data');
        setFilteredData(dataWithAverages);
      }
    }
  };
  
  // This function handles changing between M1/M2 reading types
  // with a direct, forceful approach to ensure data updates
  const handleReadingTypeChange = (newReadingType) => {
    // When switching to a specific reading type, ensure showAverage is off
    setShowAverage(false);
    
    if (actualReadingType === newReadingType) return; // Skip if already selected
    
    console.log(`Switching reading type from ${actualReadingType} to ${newReadingType}`);
    
    // 1. Update the current reading type state
    setCurrentReadingType(newReadingType);
    
    // 2. Force a remount of the chart component by altering its key
    // (done via actualReadingType in the key prop)
    
    // 3. If we're in a section view, let's directly modify the filtered data
    // to use values from the newly selected reading type
    if (selectedSection && filteredData.length > 0 && filteredSensorsBySection.length > 0) {
      console.log(`Modifying section ${selectedSection} data for ${newReadingType} reading`);
      
      // Create a new copy of filtered data with the new reading type values
      const updatedData = filteredData.map(dataPoint => {
        const result = { ...dataPoint };
        
        // For each sensor in this section, update its value to use the new reading type
        filteredSensorsBySection.forEach(sensorId => {
          // Try to get the value directly from the formatted key if it exists
          const formattedKey = `${sensorId}_${newReadingType}`;
          if (dataPoint[formattedKey] !== undefined) {
            result[sensorId] = dataPoint[formattedKey];
          }
          // Alternatively, if we can't find a formatted key, look for the data in the original data
          else if (data?.originalData) {
            const sensorDataPoints = data.originalData.filter(
              item => item.sensor === sensorId && item.timestamp === dataPoint.timestamp
            );
            
            if (sensorDataPoints.length > 0 && sensorDataPoints[0][newReadingType] !== undefined) {
              result[sensorId] = sensorDataPoints[0][newReadingType];
            }
          }
        });
        
        return result;
      });
      
      console.log('Data updated for new reading type. Sample:', updatedData.slice(0, 1));
      
      // Store the unsmoothed data first
      setOriginalFilteredData(updatedData);
      
      // Apply smoothing if enabled
      if (smoothLines && updatedData.length > 0) {
        const windowSize = updatedData.length > 300 ? 25 : 
                         (updatedData.length > 200 ? 20 : 
                         (updatedData.length > 100 ? 15 : 10));
        console.log(`Applying feature-preserving smoothing after reading type change with window size ${windowSize}`);
        const smoothedData = applySmoothing(updatedData, windowSize);
        setFilteredData(smoothedData);
      } else {
        setFilteredData(updatedData);
      }
    }
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error" variant="h6">Error loading sensor data</Typography>
        <Typography>{error.message}</Typography>
      </Box>
    );
  }
  
  // Get current sensor data
  const latestData = filteredData[filteredData.length - 1];
  const currentValue = latestData && latestData[actualReadingType] !== undefined ? latestData[actualReadingType] : null;
  const isCurrentBelowThreshold = currentValue !== null && currentValue < threshold;
  
  return (
    <Box sx={{ flexGrow: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            {/* Show tunnel name if available */}
            {selectedTunnel && (
              <span>{selectedTunnel.split('-')[0].toUpperCase()} Tunnel </span>
            )}
            {/* Show section info if available */}
            {selectedSection && (
              <span>• Section {selectedSection} </span>
            )}
            {/* Show individual sensor or general readings */}
            {sensorDetails.actualSensors && sensorDetails.actualSensors.length === 1 
              ? `• Sensor ${sensorDetails.actualSensors[0]} Details`
              : `• ${actualReadingType.toUpperCase()} Readings`
            }
          </Typography>
          
          {sensorDetails.actualSensors && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="subtitle1" color="text.secondary">
                Data from {sensorDetails.actualSensors.length} sensor{sensorDetails.actualSensors.length !== 1 ? 's' : ''}
              </Typography>
              
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {sensorDetails.actualSensors.slice(0, 5).map(sensorCode => {
                  const placement = sensorDetails.placements[sensorCode];
                  
                  // Position colors
                  const positionColors = {
                    low: '#4caf50',    // green
                    middle: '#2196f3', // blue
                    roof: '#f44336'    // red
                  };
                  
                  const color = placement?.position ? positionColors[placement.position] : '#9e9e9e';
                  
                  return (
                    <Chip
                      key={sensorCode}
                      label={sensorCode}
                      avatar={placement ? 
                        <Avatar sx={{ bgcolor: color }}>{placement.position.charAt(0).toUpperCase()}</Avatar> : 
                        undefined
                      }
                      color="primary"
                      variant="outlined"
                      size="small"
                      title={placement ? `${placement.placement} - ${placement.position}` : 'Unknown location'}
                    />
                  );
                })}
                
                {sensorDetails.actualSensors.length > 5 && (
                  <Chip
                    label={`+${sensorDetails.actualSensors.length - 5} more`}
                    color="default"
                    variant="outlined"
                    size="small"
                  />
                )}
              </Box>
            </Box>
          )}
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {data?.tunnels && (
            <Tooltip title="Filter data by tunnel">
              <FormControl sx={{ minWidth: 150, mr: 2 }} size="small">
                <InputLabel id="detail-tunnel-select-label">Tunnel</InputLabel>
                <Select
                  labelId="detail-tunnel-select-label"
                  id="detail-tunnel-select"
                  value={selectedTunnel || ''}
                  label="Tunnel"
                  onChange={(e) => changeTunnel(e.target.value || null)}
                >
                  <MenuItem value="">All Tunnels</MenuItem>
                  {data.tunnels.map(tunnel => (
                    <MenuItem key={tunnel} value={tunnel}>{tunnel}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Tooltip>
          )}
          
          {/* Section and reading type selectors removed as requested */}
          
          <Button
            variant="outlined"
            startIcon={<CompareIcon />}
            onClick={() => navigate('/comparison')}
          >
            Compare Sensors
          </Button>
        </Box>
      </Box>
      
      {/* Sensor Stats */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Current Value
              </Typography>
              <Typography variant="h4" component="div">
                {currentValue ? Math.round(currentValue) : 'N/A'}
              </Typography>
              <Box sx={{ mt: 1 }}>
                <Chip 
                  label={isCurrentBelowThreshold ? "Below Threshold" : "Above Threshold"} 
                  color={isCurrentBelowThreshold ? "error" : "success"}
                  size="small"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Average
              </Typography>
              <Typography variant="h4" component="div">
                {filteredData.length ? 
                  Math.round(filteredData
                    .filter(item => item[actualReadingType] !== null && item[actualReadingType] !== undefined)
                    .reduce((sum, item) => sum + item[actualReadingType], 0) 
                    / filteredData.filter(item => item[actualReadingType] !== null && item[actualReadingType] !== undefined).length) : 
                  'N/A'
                }
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Over selected period
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Min Value
              </Typography>
              <Typography variant="h4" component="div">
                {filteredData.length ? 
                  Math.round(Math.min(...filteredData
                    .filter(item => item[actualReadingType] !== null && item[actualReadingType] !== undefined)
                    .map(item => item[actualReadingType]))) : 
                  'N/A'
                }
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Over selected period
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Max Value
              </Typography>
              <Typography variant="h4" component="div">
                {filteredData.length ? 
                  Math.round(Math.max(...filteredData
                    .filter(item => item[actualReadingType] !== null && item[actualReadingType] !== undefined)
                    .map(item => item[actualReadingType]))) : 
                  'N/A'
                }
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Over selected period
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Chart Controls */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="time-range-label">Time Range</InputLabel>
              <Select
                labelId="time-range-label"
                id="time-range-select"
                value={timeRange}
                label="Time Range"
                onChange={handleTimeRangeChange}
              >
                {timeRanges.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          {timeRange === 'custom' && (
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Grid item xs={12} sm={4} md={3}>
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={(newDate) => setStartDate(newDate)}
                  slotProps={{ textField: { size: "small", fullWidth: true } }}
                  maxDate={endDate}
                />
              </Grid>
              
              <Grid item xs={12} sm={4} md={3}>
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={(newDate) => setEndDate(newDate)}
                  slotProps={{ textField: { size: "small", fullWidth: true } }}
                  minDate={startDate}
                  maxDate={new Date()}
                />
              </Grid>
            </LocalizationProvider>
          )}
          
          <Grid item xs={12} sm={6} md={3}>
            <FormControlLabel
              control={
                <Switch
                  checked={showThreshold}
                  onChange={(e) => setShowThreshold(e.target.checked)}
                  color="primary"
                />
              }
              label="Show Threshold"
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="wash-display-select-label">Wash Display</InputLabel>
              <Select
                labelId="wash-display-select-label"
                id="wash-display-select"
                value={washDisplayMode}
                label="Wash Display"
                onChange={(e) => setWashDisplayMode(e.target.value)}
              >
                <MenuItem value="none">No Wash Indicators</MenuItem>
                <MenuItem value="markers">Vertical Markers</MenuItem>
                <MenuItem value="line">Wash Line</MenuItem>
                <MenuItem value="both">Both Indicators</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <FormControlLabel
              control={
                <Switch
                  checked={smoothLines}
                  onChange={handleSmoothingChange}
                  color="primary"
                />
              }
              label={smoothLines ? "Smooth Data: ON" : "Smooth Data: OFF"}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <FormControlLabel
              control={
                <Switch
                  checked={cumulativeMode}
                  onChange={(e) => {
                    const newCumulativeMode = e.target.checked;
                    setCumulativeMode(newCumulativeMode);
                    
                    // Reprocess data with current settings
                    if (originalFilteredData.length > 0) {
                      console.log(`Toggling cumulative mode ${newCumulativeMode ? 'ON' : 'OFF'}`);
                      
                      // Start with original data
                      let processedData = calculateAverages([...originalFilteredData]);
                      
                      // Apply cumulative transformation if enabled
                      if (newCumulativeMode) {
                        processedData = transformToCumulativeData(processedData, relevantWashDates);
                      }
                      
                      // Apply smoothing if enabled
                      if (smoothLines) {
                        const windowSize = processedData.length > 300 ? 25 : 
                                        (processedData.length > 200 ? 20 : 
                                        (processedData.length > 100 ? 15 : 10));
                        processedData = applySmoothing(processedData, windowSize);
                      }
                      
                      // Update the data
                      setFilteredData(processedData);
                    }
                  }}
                  color="primary"
                />
              }
              label={
                <Tooltip title="Shows sensor growth without resets from washing - helps visualize true growth rate and time to reach threshold">
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <span>{cumulativeMode ? "Cumulative Mode: ON" : "Cumulative Mode: OFF"}</span>
                    <InfoIcon fontSize="small" sx={{ ml: 0.5 }} />
                  </Box>
                </Tooltip>
              }
            />
          </Grid>
          
          {showThreshold && (
            <Grid item xs={12}>
              <Box sx={{ px: 2 }}>
                <Typography id="threshold-slider" gutterBottom>
                  Threshold: {threshold}
                </Typography>
                <Slider
                  value={threshold}
                  onChange={handleThresholdChange}
                  aria-labelledby="threshold-slider"
                  valueLabelDisplay="auto"
                  min={10}
                  max={150}
                  sx={{ color: theme.palette.threshold.main }}
                />
              </Box>
            </Grid>
          )}
        </Grid>
      </Paper>
      
      {/* Show Tunnel Map for section/placement or overview */}
      <TunnelSensorMap 
        tunnelId={tunnelId}
        highlightSection={selectedSection}
        highlightSensor={sensorDetails.actualSensors?.length === 1 ? sensorDetails.actualSensors[0] : null}
        selectedSensors={selectedSensors}
        // Add sensor toggle handler
        onSensorToggle={(sensorId) => {
          console.log(`Toggling sensor ${sensorId} in chart from map click`);
          // If sensor is already selected, remove it
          if (selectedSensors.includes(sensorId)) {
            setSelectedSensors(selectedSensors.filter(s => s !== sensorId));
          } 
          // Otherwise add it
          else {
            setSelectedSensors([...selectedSensors, sensorId]);
          }
        }}
      />
      
      {/* Sensor Filter */}
      {sensorDetails.actualSensors && sensorDetails.actualSensors.length > 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>Reading Type:</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {/* M1/M2 reading options */}
                <Chip
                  label="M1 Reading"
                  color={actualReadingType === 'm1' && !showAverage ? "primary" : "default"}
                  variant={actualReadingType === 'm1' && !showAverage ? "filled" : "outlined"}
                  onClick={() => {
                    handleReadingTypeChange('m1');
                    setShowAverage(false);
                  }}
                  sx={{ cursor: 'pointer' }}
                />
                <Chip
                  label="M2 Reading"
                  color={actualReadingType === 'm2' && !showAverage ? "primary" : "default"}
                  variant={actualReadingType === 'm2' && !showAverage ? "filled" : "outlined"}
                  onClick={() => {
                    handleReadingTypeChange('m2');
                    setShowAverage(false);
                  }}
                  sx={{ cursor: 'pointer' }}
                />
                <Chip
                  label="Average (M1+M2)/2"
                  color={showAverage ? "primary" : "default"}
                  variant={showAverage ? "filled" : "outlined"}
                  onClick={() => {
                    // Force recalculation and rerender by creating a side effect
                    setShowAverage(true);
                    
                    // Force data to update with the correct reading type
                    if (filteredData.length > 0) {
                      const dataWithAverages = calculateAverages([...filteredData]);
                      if (smoothLines) {
                        const windowSize = dataWithAverages.length > 300 ? 25 : 
                                        (dataWithAverages.length > 200 ? 20 : 
                                        (dataWithAverages.length > 100 ? 15 : 10));
                        setFilteredData(applySmoothing(dataWithAverages, windowSize));
                      } else {
                        setFilteredData([...dataWithAverages]);
                      }
                    }
                  }}
                  sx={{ cursor: 'pointer' }}
                />
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle1" gutterBottom>Toggle Sensors in Chart:</Typography>
                <Box>
                  <Button 
                    variant="outlined" 
                    size="small" 
                    onClick={() => setSelectedSensors(sensorDetails.actualSensors)}
                    sx={{ mr: 1 }}
                  >
                    Select All
                  </Button>
                  <Button 
                    variant="outlined" 
                    size="small" 
                    onClick={() => clearSelectedSensors()}
                    sx={{ mr: 1 }}
                  >
                    Clear All
                  </Button>
                  <Button 
                    variant="outlined" 
                    color="primary"
                    size="small" 
                    onClick={() => clearSelectedSensors()}
                  >
                    Show Average
                  </Button>
                </Box>
              </Box>
              
              {/* Placement group selection */}
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Sensor Placements:</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {/* Get unique placements from sensor details */}
                  {[...new Set(Object.values(sensorDetails.placements || {})
                    .map(p => p.placement))]
                    .filter(Boolean)
                    .sort()
                    .map(placement => {
                      // Find all sensors for this placement
                      const placementSensors = Object.entries(sensorDetails.placements || {})
                        .filter(([_, details]) => details.placement === placement)
                        .map(([sensorId, _]) => sensorId);
                      
                      return (
                        <Chip
                          key={placement}
                          label={placement}
                          onClick={() => {
                            // Toggle selection of all sensors in this placement
                            const allSelected = placementSensors.every(s => selectedSensors.includes(s));
                            if (allSelected) {
                              // Remove all sensors in this placement
                              setSelectedSensors(selectedSensors.filter(s => !placementSensors.includes(s)));
                            } else {
                              // Add all sensors in this placement
                              setSelectedSensors([...new Set([...selectedSensors, ...placementSensors])]);
                            }
                          }}
                          color={placementSensors.some(s => selectedSensors.includes(s)) ? "primary" : "default"}
                          variant={placementSensors.every(s => selectedSensors.includes(s)) ? "filled" : "outlined"}
                          sx={{ mr: 1 }}
                        />
                      );
                    })
                  }
                </Box>
              </Box>
            </Grid>
          </Grid>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
            {/* Show individual sensors with their positions */}
            {sensorDetails.actualSensors.map(sensorCode => {
              const placement = sensorDetails.placements[sensorCode];
              const positionColors = {
                low: '#4caf50',    // green
                middle: '#2196f3', // blue
                roof: '#f44336'    // red
              };
              const color = placement?.position ? positionColors[placement.position] : '#9e9e9e';
              const isSelected = selectedSensors.includes(sensorCode);
              
              return (
                <Chip
                  key={sensorCode}
                  label={sensorCode}
                  avatar={placement ? 
                    <Avatar sx={{ bgcolor: color }}>{placement.position.charAt(0).toUpperCase()}</Avatar> : 
                    undefined
                  }
                  onClick={() => {
                    // Toggle this sensor in the selected sensors list
                    if (isSelected) {
                      setSelectedSensors(selectedSensors.filter(s => s !== sensorCode));
                    } else {
                      setSelectedSensors([...selectedSensors, sensorCode]);
                    }
                  }}
                  color={isSelected ? "primary" : "default"}
                  variant={isSelected ? "filled" : "outlined"}
                  sx={{ cursor: 'pointer' }}
                  title={placement ? `${placement.placement} - ${placement.position}` : 'Unknown location'}
                />
              );
            })}
          </Box>
        </Paper>
      )}
      
      {/* Chart Section */}
      <Paper sx={{ p: 2, height: 500, mb: 3 }}>
        {filteredData.length > 0 ? (
          <SensorTimeSeries 
            // Force remount when reading type, average mode, or cumulative mode changes to guarantee chart update
            key={`sensor-chart-${showAverage ? 'avg' : actualReadingType}-${showAverage}-${userClearedSelection ? 'avg' : 'individual'}-${cumulativeMode ? 'cumulative' : 'standard'}`}
            data={filteredData}
            sensorId={showAverage ? 'avg' : actualReadingType}
            selectedSensors={selectedSensors}
            sensorDetails={sensorDetails}
            threshold={showThreshold ? threshold : null}
            // Using properly sanitized data from the API - no need for additional filtering
            washDates={washDisplayMode === 'none' ? [] : relevantWashDates}
            washTypes={washTypes}
            height={450}
            smooth={smoothLines}
            showWashLine={washDisplayMode === 'line' || washDisplayMode === 'both'}
          />
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Typography variant="subtitle1">No data available for the selected time range</Typography>
          </Box>
        )}
      </Paper>
      
      {/* More Actions */}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4}>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<CloudDownloadIcon />}
            onClick={() => {/* Export data functionality */}}
          >
            Export Data
          </Button>
        </Grid>
        
        <Grid item xs={12} sm={6} md={4}>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<TuneIcon />}
            onClick={() => {/* Additional settings */}}
          >
            Advanced Settings
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SensorDetailView;